import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Modal from '@mui/material/Modal';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { equals } from 'ramda';
import React from 'react';
// import { useHistory } from 'react-router';

// import { ALL_NOTIFICATIONS_ROUTE } from 'src/route_constants';
import { getTheme } from 'src/utilities/siteSettings';
import { mapIndexed } from 'src/utilities/visualFieldsMapper';

import InAppMessage from './InAppMessage';
import MarkAsComplete from 'src/features/Users/Works/WorkGroups/WorkTile/MarkAsComplete';
import { useNotificationStyles } from './notifications.styles';

interface Props {
  handleReadMessage: (n: any, i: number, isMarkAsRead: boolean) => void;
  isOpen: any;
  setIsOpen: any;
  unReadMsgCount: any;
  unReadNotifications: any;
  onClearAll: () => void;
}

interface DesktopNotificationProps {
  close: () => void;
  handleReadMessage: any;
  handleMarkAsRead: (n: any, i: number) => void;
  open: boolean;
  unReadMsgCount: number;
  unReadNotifications: any;
  onClearAll: () => void;
}

interface MobileNotificationProps {
  close: () => void;
  handleReadMessage: any;
  handleMarkAsRead: (n: any, i: number) => void;
  open: boolean;
  unReadMsgCount: number;
  unReadNotifications: any;
  onClearAll: () => void;
}

const theme = getTheme();


const TextButton = styled(Button)({
  all: 'unset',
  background: 'none',
  border: 'none',
  color: theme.palette.primaryColor,
  cursor: 'pointer',
  display: 'inline',
  font: 'inherit',
  margin: 0,
  padding: 0,
  textAlign: 'center',
  width: '100%',
});


const InAppNotificationTitle = (props: {
  unReadMsgCount: number;
  handleClearAll: () => void;
}) => {
  const { unReadMsgCount, handleClearAll } = props;
  const classes = useNotificationStyles()

  return (<Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 32px',
    paddingBottom: '0px',
  }}>
    <Typography
      flex={1}
      fontSize={'16px'}
      fontWeight={600}
      variant="caption"
      mt={1}
    >
      Notifications
    </Typography>
    <Box mt={1} className={classes.menuIconCount}>
      {unReadMsgCount}&nbsp;
    </Box>
    <Box onClick={() => handleClearAll()}>
      <MarkAsComplete toolTipText={"Mark all as Read"} labelText={"Clear all"} handleMarkAsComplete={() => { }} />
    </Box>
  </Box>)
}

const InAppNotificationDrawer = ({
  close,
  handleReadMessage,
  open,
  unReadMsgCount = 0,
  unReadNotifications,
  handleMarkAsRead,
  onClearAll
}: DesktopNotificationProps) => {

  return (
    <Drawer anchor="right" onClose={close} open={open}>
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          height: '100%',
          overflow: 'scroll',
          width: '400px',
        }}
      >
        <InAppNotificationTitle
          unReadMsgCount={unReadMsgCount}
          handleClearAll={onClearAll}
        />

        {mapIndexed((unreadNotification: any, index: number) => {
          return (
            <InAppMessage
              handleReadMessage={(notif) => handleReadMessage(notif, index)}
              handleMarkAsRead={(notif) => handleMarkAsRead(notif, index)}
              key={index}
              notification={unreadNotification}
            />
          );
        }, unReadNotifications)}
        {equals(unReadMsgCount, 0) && (
          <Box py="24px" sx={{ textAlign: 'center' }} width={'100%'}>
            <Typography
              color={theme.palette.gray.softGray}
              fontSize={'12px'}
              variant="caption"
            >
              {`You've read all your notifications`}
            </Typography>
          </Box>
        )}
      </Box>
      {/* INFO: Temporarily disabled 
          Currently this takes user to a page that is behind access control
          Need to finalise if this feature is required or not
          */}
      {/* <Box
        alignItems="center"
        border={`1px solid ${theme.palette.primaryColor}`}
        borderRadius={'5px'}
        display="flex"
        justifyContent="center"
        mt={'24px'}
        mx="32px"
        my="26px"
      >
        <Button onClick={goToAllNotifications} sx={{ padding: "14.5px 0px", width: "100%" }}>
          <Typography
            color={theme.palette.primaryColor}
            fontSize={'14px'}
            variant="caption"
          >
            Go to All Notifications
          </Typography>
        </Button>
      </Box> */}
    </Drawer>
  );
};

const InAppNotificationMobile = ({
  close,
  handleReadMessage,
  open,
  unReadMsgCount,
  unReadNotifications,
  handleMarkAsRead,
  onClearAll
}: MobileNotificationProps) => {


  return (
    <Modal
      aria-describedby="modal-modal-description"
      aria-labelledby="modal-modal-title"
      onClose={close}
      open={open}
    >
      <Box
        sx={{
          bgcolor: 'basic.white',
          left: '50%',
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '367px',
        }}
      >
        <InAppNotificationTitle
          unReadMsgCount={unReadMsgCount}
          handleClearAll={onClearAll}
        />
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            maxHeight: '75vh',
            overflow: 'scroll',
          }}
        >
          {mapIndexed((unreadNotification: any, index: number) => {
            return (
              <InAppMessage
                handleReadMessage={(notif) => handleReadMessage(notif, index)}
                handleMarkAsRead={(notif) => handleMarkAsRead(notif, index)}
                key={index}
                notification={unreadNotification}
              />
            );
          }, unReadNotifications)}
          {equals(unReadMsgCount, 0) && (
            <Box py="24px" sx={{ textAlign: 'center' }} width={'100%'}>
              <Typography
                color={theme.palette.gray.softGray}
                fontSize={'12px'}
                variant="caption"
              >
                {`You've read all your notifications`}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          height="45px"
          sx={{
            display: 'flex',
            placeContent: 'center',
          }}
        >
          <TextButton onClick={close}>Close</TextButton>
        </Box>
      </Box>
    </Modal>
  );
};

const InAppNotification: React.FC<Props> = (props: Props) => {
  const {
    handleReadMessage,
    isOpen,
    setIsOpen,
    unReadMsgCount,
    unReadNotifications,
    onClearAll
  } = props;

  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const open = Boolean(isOpen);

  return isMobile ? (
    <InAppNotificationMobile
      close={() => setIsOpen(false)}
      handleReadMessage={handleReadMessage}
      handleMarkAsRead={(n: any, index: number) => handleReadMessage(n, index, true)}
      onClearAll={onClearAll}
      open={open}
      unReadMsgCount={unReadMsgCount}
      unReadNotifications={unReadNotifications}
    />
  ) : (
    <InAppNotificationDrawer
      close={() => setIsOpen(false)}
      handleReadMessage={handleReadMessage}
      handleMarkAsRead={(n: any, index: number) => handleReadMessage(n, index, true)}
      onClearAll={onClearAll}
      open={open}
      unReadMsgCount={unReadMsgCount}
      unReadNotifications={unReadNotifications}
    />
  );
};

export default InAppNotification;
